import * as React from 'react';
import { createStyles, TextInput as TextInputBase, TextInputProps } from '@mantine/core';
import { useTranslation } from 'react-i18next';

// Components
import { FieldLabelTooltip } from './FieldLabelTooltip';
import { InterpolatedFormCopy } from './InterpolatedFormCopy';

// Styles
import { getCommonFieldStyles } from '../helpers/fieldStyles';

// Hooks
import { useFieldTranslation } from '../hooks/useFieldTranslation';
import { usePermission } from '../../../hooks/usePermission';

// Types
import { GenericFieldProps } from '../types';

const useStyles = createStyles((theme) => {
    const commonFieldStyles = getCommonFieldStyles(theme);

    return {
        root: commonFieldStyles.root,
        input: commonFieldStyles.input,
        readonlyInput: commonFieldStyles.readonlyInput,
        required: commonFieldStyles.required,
        hiddenDisplay: commonFieldStyles.hiddenDisplay,
        label: commonFieldStyles.label,
        icon: commonFieldStyles.icon,
        error: commonFieldStyles.error,
        wrapper: commonFieldStyles.wrapper,
        readonlyWrapper: commonFieldStyles.readonlyWrapper,
        description: commonFieldStyles.description
    };
});

export interface CustomTextInputProps extends TextInputProps, GenericFieldProps {
    key?: string;
    value?: string;
}

export const TextInput: React.FC<CustomTextInputProps> = ({
    readOnly,
    labelKey,
    descriptionKey,
    label,
    tooltipKey,
    placeholderkey,
    parentform,
    targetperiod,
    value = '',
    writepermission,
    ...props
}) => {
    const { t } = useTranslation();
    const { classes, cx } = useStyles();
    const inputLabel = useFieldTranslation({
        key: labelKey,
        label,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });
    const descriptionLabel = useFieldTranslation({
        key: descriptionKey,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });
    const placeholderLabel = useFieldTranslation({
        key: placeholderkey,
        formData: parentform?.values,
        noInterpolation: true
    }) as string;
    const hasWritePermission = usePermission(writepermission);

    const overrideProps = React.useMemo(
        () =>
            hasWritePermission === false
                ? {
                      disabled: true,
                      description: t('notPermitted', { ns: 'common' })
                  }
                : {},
        [hasWritePermission]
    );

    return (
        <TextInputBase
            classNames={{
                root: classes.root,
                input: cx(classes.input, {
                    [classes.readonlyInput]: readOnly
                }),
                required: cx(classes.required, {
                    [classes.hiddenDisplay]: readOnly
                }),
                label: classes.label,
                icon: cx(classes.icon, {
                    [classes.hiddenDisplay]: readOnly
                }),
                error: classes.error,
                wrapper: cx(classes.wrapper, {
                    [classes.readonlyWrapper]: readOnly
                }),
                description: cx(classes.description, {
                    [classes.hiddenDisplay]: readOnly
                })
            }}
            label={
                <>
                    <span>{inputLabel}</span>
                    {tooltipKey ? <FieldLabelTooltip translationKey={tooltipKey} /> : null}
                </>
            }
            description={descriptionLabel}
            placeholder={placeholderLabel}
            autoComplete="off"
            value={value}
            {...props}
            {...overrideProps}
        />
    );
};
